<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <div class="col-12 d-flex flex-wrap align-items-center mb-2">
               <!-- Report selector -->
               <div class="d-flex align-items-center me-auto">
                  <span class="p-1 filterTitle">Report:</span>
                  <select v-model="chosenReport" class="form-select shadow-none filterOptions bordered-select" style="font-size: 13px;">
                     <option v-for="f in filterByList" :value="f.value" :key="f.value">{{ f.text }}</option>
                  </select>
               </div>

               <!-- Record count -->
               <div class="d-flex align-items-center">
                  <span class="text-muted" style="font-size: 13px;">
                     <strong>{{ reports.length }}</strong> records
                  </span>
               </div>
            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col" v-for="header in headers[chosenReport]" :key="header.value">
                        {{ header.text }}
                     </th>
                  </tr>
               </thead>

               <tbody v-if="reports.length <= 0">
                  <tr>
                     <td colspan="3">No records to display.</td>
                  </tr>
               </tbody>

               <!-- Active Accounts -->
               <tbody v-if="chosenReport == 'active_accounts'">
                  <tr v-for="(report, index) in reports" :key="index">
                     <td>{{ report.account_name }}</td>
                     <td class="date-text">{{ formatDate(report.date_created) }}</td>
                     <td>
                        <span class="activity-pill" :class="getActivityClass(report.days_active)">
                           <i :class="getActivityIcon(report.days_active)" class="me-1"></i>
                           {{ report.days_active }} day(s) ago
                        </span>
                     </td>
                  </tr>
               </tbody>

               <!-- Active Devices -->
               <tbody v-if="chosenReport == 'active_devices'">
                  <tr v-for="(report, index) in reports" :key="index">
                     <td>{{ report.account_name }}</td>
                     <td class="date-text">{{ formatDate(report.date_created) }}</td>
                     <td>
                        <span class="device-badge">
                           <i class="fa-solid fa-desktop me-1"></i>
                           {{ report.active_devices }} device(s)
                        </span>
                     </td>
                  </tr>
               </tbody>

               <!-- Inactive Accounts -->
               <tbody v-if="chosenReport == 'inactive_account'">
                  <tr v-for="(report, index) in reports" :key="index">
                     <td>{{ report.account_name }}</td>
                     <td class="date-text">{{ formatDate(report.date_created) }}</td>
                     <td>
                        <span class="device-badge device-inactive">
                           <i class="fa-solid fa-desktop me-1"></i>
                           {{ report.inactive_devices }} device(s)
                        </span>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>
</template>

<script>
import moment from 'moment'
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {
   data() {
      return {
         headers: {
            "active_accounts": [
               { text: "Account Name", value: "accountName" },
               { text: "Date", value: "dateAndTime" },
               { text: "Last Active", value: "lastActiveDay" },
            ],
            "active_devices": [
               { text: "Account Name", value: "accountName" },
               { text: "Date", value: "dateAndTime" },
               { text: "Active Devices", value: "activeDeviceCount" },
            ],
            "inactive_accounts": [
               { text: "Account Name", value: "accountName" },
               { text: "Date", value: "dateAndTime" },
               { text: "Inactive Devices", value: "activeDeviceCount" },
            ],
         },
         chosenReport: "active_accounts",
         filterByList: [
            { text: "Active Accounts", value: "active_accounts" },
            { text: "Inactive Devices", value: "inactive_accounts" },
            { text: "Active Devices", value: "active_devices" },
         ],
         search: "",
         reports: [],
         debounceFn: null,
         paginationData: {},
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         sorting: { field: "name", order: "desc" },
      }
   },

   computed: {
      reportType() { return this.chosenReport },
   },

   watch: {
      reportType() { this.getVertexReport() },
   },

   created() {
      this.debounceFn = debounce(() => this.getVertexReport(), 500)
      this.getVertexReport()
   },

   methods: {
      formatDate(date) { return moment(date).format("DD-MMM-YYYY, hh:mm A") },

      getActivityClass(days) {
         if (days <= 1) return 'activity-recent';
         if (days <= 7) return 'activity-moderate';
         return 'activity-stale';
      },

      getActivityIcon(days) {
         if (days <= 1) return 'fa-solid fa-circle-check';
         if (days <= 7) return 'fa-solid fa-clock';
         return 'fa-solid fa-triangle-exclamation';
      },

      async getVertexReport() {
         this.reports = []
         let apiPath = "admin-controls/get-vertex-report?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page + "&report_type=" + this.chosenReport

         if (this.sorting.order != "") {
            apiPath += "&order_by=" + this.sorting.order
         }
         if (this.search !== "" || this.search !== null) {
            apiPath += "&search=" + this.search
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.report_data === "No Record Found")
                  this.reports = []
               else {
                  this.reports = response.data.report_data
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load admin report data."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }
.date-text { font-size: 13px; color: #6c757d; white-space: nowrap; }

/* Activity pills */
.activity-pill {
   padding: 4px 10px; border-radius: 6px;
   font-size: 11px; font-weight: 600;
   display: inline-flex; align-items: center; white-space: nowrap;
}
.activity-recent { background: #e6f9ee; color: #00873c; }
.activity-moderate { background: #e0f7fa; color: #00838f; }
.activity-stale { background: #fff8e1; color: #e6a100; }

/* Device badge */
.device-badge {
   font-size: 12px; font-weight: 600;
   padding: 4px 10px; border-radius: 6px;
   display: inline-flex; align-items: center;
   background: #e8eaf6; color: #3949ab;
}
.device-inactive { background: #fde8ea; color: #c62828; }
</style>